import { BrandMenu } from '@prismamedia/one-components';
import { FC, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { BrandKey } from '../../__generated__/queries-web';
import { useGetBrandKey } from '../../apollo/sharedQueries/getBrandKeys.web.graphql';
import { getBrandOptions } from '../../utils/brands';

const ALL_ID = 'ALL';

interface BrandSwitchProps {
  brandKey?: BrandKey;
  setBrandKey?: (brandKey: BrandKey | undefined) => void;
}

export const BrandSwitch: FC<BrandSwitchProps> = ({
  brandKey,
  setBrandKey,
}) => {
  const { data } = useGetBrandKey();
  const history = useHistory();
  const location = useLocation();

  const brandKeyItemList = useMemo(
    () =>
      // eslint-disable-next-line fp/no-mutating-methods
      (data?.categories || [])
        .filter((cat) => !getBrandOptions(cat.brandKey).disabledForCerise)
        .sort((a, b) => a.brandKey.localeCompare(b.brandKey))
        .map(({ brandKey: label }) => ({
          id: label,
          label,
          selected: brandKey === label,
        })) || [],
    [brandKey, data],
  );

  useEffect(() => {
    if (!brandKey && data?.categories.length === 1) {
      setBrandKey?.(data.categories[0]?.brandKey);
    }
  }, [data, brandKey, setBrandKey]);

  const brandKeyItemListWithAll = [
    {
      id: ALL_ID,
      label: 'Toutes',
      selected: !brandKey,
    },
    ...brandKeyItemList,
  ];

  // Check if we're in a Pages context by looking at the current path
  const isPagesContext = location.pathname.includes('/page/');

  const handleBrandSelect = (item: { id?: string }) => {
    const newBrandKey = item.id === ALL_ID ? undefined : (item.id as BrandKey);

    // Update the global brand key
    setBrandKey?.(newBrandKey);

    // If we're in Pages context, handle navigation
    if (isPagesContext) {
      const pathSegments = location.pathname.split('/');
      const formatIndex =
        pathSegments.findIndex((segment) => segment === 'page') + 1;
      const format = pathSegments[formatIndex] || 'category';

      if (newBrandKey) {
        // Navigate to the list view with the new brand
        const newPath = `/${newBrandKey}/page/${format}/list`;
        history.push(newPath);
      } else {
        // Navigate to brand selection if "Toutes" is selected
        const newPath = `/page/${format}/list`;
        history.push(newPath);
      }
    }
  };

  return (
    <BrandMenu
      defaultLabel="Marque"
      items={brandKeyItemListWithAll}
      onItemSelect={handleBrandSelect}
    />
  );
};
